import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { userService, type UserProfile } from '../../services/userService';

export function Profile() {
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [signature, setSignature] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    void load();
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const p = await userService.getCurrentProfile();
      setProfile(p);
      const sig = p?.preferences?.email_signature_html;
      setSignature(typeof sig === 'string' ? sig : '');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    const toastId = toast.loading('Saving signature…');
    try {
      await userService.setEmailSignature(signature);
      toast.success('Email signature saved', { id: toastId });
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : 'Failed to save signature',
        { id: toastId },
      );
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <p className="text-sm text-gray-500">Loading…</p>;
  }

  return (
    <div className="space-y-6 max-w-4xl">
      <section>
        <h2 className="text-lg font-semibold text-gray-900">Profile</h2>
        <dl className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
          <div>
            <dt className="text-gray-500">Name</dt>
            <dd className="text-gray-900">{profile?.name ?? '—'}</dd>
          </div>
          <div>
            <dt className="text-gray-500">Email</dt>
            <dd className="text-gray-900">{profile?.email ?? '—'}</dd>
          </div>
        </dl>
      </section>

      <section>
        <h2 className="text-lg font-semibold text-gray-900">Email Signature</h2>
        <p className="mt-1 text-sm text-gray-600">
          Appended to client-facing emails sent from the app (e.g. ACORD certificates,
          contracts for review). Paste raw HTML — images referenced via external URL or
          inline data-URI both work.
        </p>

        <label htmlFor="signature" className="mt-4 block text-sm font-medium text-gray-700">
          HTML
        </label>
        <textarea
          id="signature"
          value={signature}
          onChange={(e) => setSignature(e.target.value)}
          rows={14}
          spellCheck={false}
          className="mt-1 w-full font-mono text-xs px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
          placeholder="<table>...</table>"
        />

        <div className="mt-4">
          <p className="text-sm font-medium text-gray-700 mb-2">Preview</p>
          <div className="border border-gray-200 rounded-md p-4 bg-white">
            {signature ? (
              <div dangerouslySetInnerHTML={{ __html: signature }} />
            ) : (
              <p className="text-sm text-gray-400 italic">No signature set.</p>
            )}
          </div>
        </div>

        <div className="mt-4 flex justify-end">
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-md hover:bg-primary-700 disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Save Signature'}
          </button>
        </div>
      </section>
    </div>
  );
}
